(() => {
  const rootSelector = '[data-labor-product]';

  function laborFetch(url, options) {
    const fn = window.__originalFetch || window.fetch;
    return fn.call(window, url, options);
  }

  function cartJsUrl(route, fallback) {
    const raw = typeof route === 'string' && route.length ? route : fallback;
    return raw.endsWith('.js') ? raw : `${raw}.js`;
  }

  function shopifyRootPath() {
    const raw = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (typeof raw !== 'string' || !raw.length) return '/';
    return raw.endsWith('/') ? raw : `${raw}/`;
  }

  function getCheckoutUrl() {
    const raw = window.routes && window.routes.cart_url;
    if (typeof raw !== 'string' || !raw.length) return '/checkout';
    try {
      const u = new URL(raw, window.location.origin);
      u.pathname = u.pathname.replace(/\/cart\/?$/, '/checkout');
      return u.pathname + u.search;
    } catch (e) {
      return '/checkout';
    }
  }

  function wantsDrawer(root) {
    return root.getAttribute('data-open-drawer') === 'true';
  }

  function getConfiguredDiscountCode(root) {
    const el = root.querySelector('[data-labor-discount-code]');
    return String((el && el.getAttribute('data-labor-discount-code')) || '').trim();
  }

  function getDiscountCode(root) {
    const input = selectedInput(root);
    if (!input || input.getAttribute('data-labor-apply-discount') !== 'true') return '';
    return getConfiguredDiscountCode(root);
  }

  function discountCodesFromCart(cart) {
    const out = [];
    const seen = new Set();
    const push = (raw) => {
      const code = String(raw ?? '').trim();
      if (!code) return;
      const key = code.toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(code);
    };
    if (cart && Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach((entry) => {
        push(typeof entry === 'string' ? entry : entry && entry.code);
      });
    }
    if (cart && typeof cart.discount_code === 'string') push(cart.discount_code);
    return out;
  }

  async function mergeCartDiscountCodes(nextCode) {
    const code = String(nextCode ?? '').trim();
    let existing = [];
    try {
      const res = await laborFetch(cartJsUrl(window.routes?.cart_url, '/cart'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) existing = discountCodesFromCart(await res.json());
    } catch (e) {
      /* still apply the new code */
    }
    if (!code) return existing;
    const upper = code.toUpperCase();
    if (existing.some((c) => c.toUpperCase() === upper)) return existing;
    return existing.concat(code);
  }

  function getCheckoutUrlWithDiscountCodes(codes) {
    const checkout = getCheckoutUrl();
    const list = (Array.isArray(codes) ? codes : [])
      .map((c) => String(c ?? '').trim())
      .filter(Boolean);
    if (!list.length) return checkout;
    const encoded = list.map((c) => encodeURIComponent(c)).join(',');
    return `${shopifyRootPath()}discount/${encoded}?redirect=${encodeURIComponent(checkout)}`;
  }

  function cartHasApplicableCode(cart, code) {
    const upper = String(code ?? '').trim().toUpperCase();
    if (!upper) return true;
    const entries = Array.isArray(cart && cart.discount_codes) ? cart.discount_codes : [];
    return entries.some((entry) => {
      const name = String(
        typeof entry === 'string' ? entry : entry && entry.code ? entry.code : ''
      ).toUpperCase();
      if (name !== upper) return false;
      if (entry && typeof entry === 'object' && 'applicable' in entry) {
        return Boolean(entry.applicable);
      }
      return true;
    });
  }

  async function pingDiscountEndpoint(codes) {
    const list = (Array.isArray(codes) ? codes : [])
      .map((c) => String(c ?? '').trim())
      .filter(Boolean);
    if (!list.length) return false;
    const url = `${shopifyRootPath()}discount/${list.map((c) => encodeURIComponent(c)).join(',')}`;
    try {
      await laborFetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  async function stripCartDiscountCode(code, fallbackSections) {
    const toStrip = String(code ?? '').trim();
    if (!toStrip) return fallbackSections;
    let existing = [];
    try {
      const res = await laborFetch(cartJsUrl(window.routes?.cart_url, '/cart'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) existing = discountCodesFromCart(await res.json());
    } catch (e) {
      return fallbackSections;
    }
    const upper = toStrip.toUpperCase();
    if (!existing.some((c) => c.toUpperCase() === upper)) return fallbackSections;
    const remaining = existing.filter((c) => c.toUpperCase() !== upper);
    await setFreeShippingLabelAttribute(false);
    try {
      const res = await laborFetch(cartJsUrl(window.routes?.cart_update_url, '/cart/update'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: remaining.join(','),
          sections: 'cart-drawer,cart-icon-bubble',
        }),
      });
      if (!res.ok) return fallbackSections;
      const cart = await res.json().catch(() => null);
      return (cart && cart.sections) || fallbackSections;
    } catch (e) {
      return fallbackSections;
    }
  }

  // Cart attribute read by the free-shipping label delivery customization:
  // "1" when the free-shipping code is confirmed on the cart, cleared otherwise.
  async function setFreeShippingLabelAttribute(on) {
    try {
      await laborFetch(cartJsUrl(window.routes?.cart_update_url, '/cart/update'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { free_shipping_label: on ? '1' : '' } }),
      });
    } catch (e) {}
  }

  async function applyCartDiscountCode(discountCode, fallbackSections) {
    const code = String(discountCode ?? '').trim();
    if (!code) return fallbackSections;
    const merged = await mergeCartDiscountCodes(code);
    try {
      const res = await laborFetch(cartJsUrl(window.routes?.cart_update_url, '/cart/update'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: merged.join(','),
          sections: 'cart-drawer,cart-icon-bubble',
        }),
      });
      if (!res.ok) {
        await pingDiscountEndpoint(merged);
        return fallbackSections;
      }
      const cart = await res.json().catch(() => null);
      const applicable = cartHasApplicableCode(cart, code);
      if (!applicable) {
        await pingDiscountEndpoint(merged);
      }
      await setFreeShippingLabelAttribute(applicable);
      return (cart && cart.sections) || fallbackSections;
    } catch (e) {
      await pingDiscountEndpoint(merged);
      return fallbackSections;
    }
  }

  async function redirectToCheckout(discountCode) {
    const code = String(discountCode ?? '').trim();
    if (!code) {
      window.location.href = getCheckoutUrl();
      return;
    }
    const merged = await mergeCartDiscountCodes(code);
    window.location.href = getCheckoutUrlWithDiscountCodes(merged);
  }

  function parseGiftIds(root) {
    const raw = root.getAttribute('data-gift-ids') || '';
    if (!raw) return [];
    return raw
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => id > 0);
  }

  function selectedInput(root) {
    return root.querySelector('.labor-swatch__input:checked');
  }

  const THUMB_OFFSET = 32;

  function refreshLaborGallery(galleryRoot) {
    const api = galleryRoot && galleryRoot._laborGallery;
    if (!api) return;
    api.swiperMain.slideTo(0, 0);
    api.swiperThumb.slideTo(0, 0);
    requestAnimationFrame(() => {
      api.lockGalleryHeight();
      api.syncThumbNav();
      api.swiperMain.update();
      api.swiperThumb.update();
    });
  }

  function initLaborGallery(galleryRoot) {
    if (!galleryRoot || galleryRoot.hidden) return;
    if (galleryRoot.dataset.laborGalleryReady === 'true') {
      refreshLaborGallery(galleryRoot);
      return;
    }
    const thumbEl = galleryRoot.querySelector('.mySwiperThumb');
    const mainEl = galleryRoot.querySelector('.mySwiperMain');
    if (!thumbEl || !mainEl || typeof Swiper === 'undefined') return;

    const swiperThumb = new Swiper(thumbEl, {
      direction: 'horizontal',
      spaceBetween: 0,
      slidesPerView: 'auto',
      watchSlidesProgress: true,
      watchOverflow: false,
      slideToClickedSlide: true,
      slidesOffsetBefore: THUMB_OFFSET,
      slidesOffsetAfter: THUMB_OFFSET,
      navigation: {
        nextEl: thumbEl.querySelector('.mySwiperThumb-next'),
        prevEl: thumbEl.querySelector('.mySwiperThumb-prev'),
      },
    });

    const swiperMain = new Swiper(mainEl, {
      loop: false,
      rewind: true,
      autoHeight: false,
      slidesPerView: 1,
      spaceBetween: 0,
      navigation: {
        nextEl: mainEl.querySelector('.custom-button-next'),
        prevEl: mainEl.querySelector('.custom-button-prev'),
      },
      thumbs: {
        swiper: swiperThumb,
      },
    });

    function thumbsFitTrack() {
      const slides = thumbEl.querySelectorAll('.swiper-wrapper > .swiper-slide');
      if (!slides.length) return true;
      const gap = 10;
      let needed = 0;
      slides.forEach((slide, index) => {
        needed += slide.offsetWidth;
        if (index > 0) needed += gap;
      });
      return needed <= thumbEl.clientWidth + 1;
    }

    function syncThumbNav() {
      const fits = thumbsFitTrack();
      thumbEl.classList.toggle('is-thumbs-fit', fits);
      const offset = fits ? 0 : THUMB_OFFSET;
      swiperThumb.params.slidesOffsetBefore = offset;
      swiperThumb.params.slidesOffsetAfter = offset;
      swiperThumb.update();
    }

    function lockGalleryHeight() {
      const leadImg =
        mainEl.querySelector('.swiper-slide.is-lead:not(.swiper-slide-duplicate) img') ||
        mainEl.querySelector('.swiper-slide.is-lead img');
      if (!leadImg) return;
      const width = leadImg.clientWidth;
      const naturalW = leadImg.naturalWidth;
      const naturalH = leadImg.naturalHeight;
      let height = 0;
      if (width && naturalW && naturalH) {
        height = Math.round(width * (naturalH / naturalW));
      } else {
        height = leadImg.offsetHeight;
      }
      if (!height) return;
      mainEl.style.setProperty('--gallery-slide-height', height + 'px');
      swiperMain.update();
    }

    galleryRoot._laborGallery = { swiperMain, swiperThumb, lockGalleryHeight, syncThumbNav };
    galleryRoot.dataset.laborGalleryReady = 'true';

    mainEl.querySelectorAll('.swiper-slide.is-lead img').forEach((img) => {
      if (img.complete) lockGalleryHeight();
      else img.addEventListener('load', lockGalleryHeight, { once: true });
    });
    window.addEventListener('resize', () => {
      if (galleryRoot.hidden) return;
      lockGalleryHeight();
      syncThumbNav();
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        lockGalleryHeight();
        syncThumbNav();
        swiperMain.update();
      });
    });
  }

  function syncGallery(root) {
    const input = selectedInput(root);
    const productId = input && input.getAttribute('data-product-id');
    const galleries = root.querySelectorAll('[data-labor-gallery]');
    if (!galleries.length) return;

    let active = null;
    galleries.forEach((gallery) => {
      const match = productId && gallery.getAttribute('data-labor-gallery') === productId;
      gallery.hidden = !match;
      if (match) active = gallery;
    });

    if (!active) {
      active = galleries[0];
      active.hidden = false;
    }

    initLaborGallery(active);
  }

  function syncCta(root) {
    const input = selectedInput(root);
    const compareEl = root.querySelector('[data-labor-cta-compare]');
    const priceEl = root.querySelector('[data-labor-cta-price]');
    if (!input) return;
    const price = input.getAttribute('data-price') || '';
    const compare = input.getAttribute('data-compare-price') || '';
    if (priceEl) priceEl.textContent = price;
    if (compareEl) {
      compareEl.textContent = compare;
      compareEl.hidden = !compare;
    }
  }

  function syncStickyMedia(root) {
    const input = (root && selectedInput(root)) || document.querySelector('.labor-swatch__input:checked');
    const url = input && input.getAttribute('data-labor-image');
    if (!url) return;
    const alt = (input && input.getAttribute('data-labor-image-alt')) || '';
    document.querySelectorAll('.sticky-foundation-bundle__media').forEach((media) => {
      let img = media.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        media.appendChild(img);
      }
      if (img.getAttribute('src') !== url) {
        img.src = url;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
      }
      if (alt) img.alt = alt;
      media.hidden = false;
    });
  }

  function setLoading(button, isLoading) {
    if (!button) return;
    if (isLoading) {
      if (!button.dataset.originalHtml) button.dataset.originalHtml = button.innerHTML;
      button.disabled = true;
      button.classList.add('is-loading');
    } else {
      if (button.dataset.originalHtml) {
        button.innerHTML = button.dataset.originalHtml;
        delete button.dataset.originalHtml;
      }
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  function resetLaborCtas() {
    document.querySelectorAll('[data-labor-cta]').forEach((button) => {
      setLoading(button, false);
    });
  }

  function showError(root, message) {
    const el = root.querySelector('[data-labor-cta-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  function openCartDrawerFallback() {
    const el = document.querySelector('cart-drawer');
    if (!el) return;
    el.classList.add('active');
    el.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    const inner = el.querySelector('.drawer__inner');
    if (inner) inner.style.setProperty('transform', 'translateX(0)', 'important');
  }

  function applyCartSections(sections) {
    if (sections && window.cartDrawerCustom && typeof window.cartDrawerCustom.applySections === 'function') {
      window.cartDrawerCustom.applySections(sections);
      return true;
    }
    return false;
  }

  function openCartDrawer(sections) {
    applyCartSections(sections);
    if (window.cartDrawerCustom && typeof window.cartDrawerCustom.open === 'function') {
      // viewOnly=true skips the product metafield /pages force-checkout
      // path so this section's checkbox can actually show the drawer.
      // Do not call refresh() here — that runs cart-free-gifts.sync() and
      // would strip the metafield gifts we just added.
      window.cartDrawerCustom.open(true);
      return Promise.resolve();
    }
    openCartDrawerFallback();
    return Promise.resolve();
  }

  function initRoot(root) {
    if (!root || root.dataset.laborReady === 'true') return;
    root.dataset.laborReady = 'true';
    syncCta(root);
    syncGallery(root);
    syncStickyMedia(root);
  }

  function initAll(scope) {
    const base = scope || document;
    if (base.matches?.(rootSelector)) initRoot(base);
    base.querySelectorAll?.(rootSelector).forEach(initRoot);
  }

  document.addEventListener('change', (event) => {
    const input = event.target.closest('.labor-swatch__input');
    if (!input) return;
    const root = input.closest(rootSelector);
    if (root) {
      syncCta(root);
      syncGallery(root);
      syncStickyMedia(root);
    }
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-labor-cta]');
    if (!button || button.disabled) return;

    const root = button.closest(rootSelector);
    if (!root) return;

    event.preventDefault();
    event.stopPropagation();

    const input = selectedInput(root);
    const variantId = parseInt(input && input.value, 10);
    if (!variantId) {
      showError(root, 'Please select a bundle.');
      return;
    }

    const sellingPlanId = parseInt(input.getAttribute('data-selling-plan-id'), 10);
    const items = [{ id: variantId, quantity: 1 }];
    if (sellingPlanId > 0) items[0].selling_plan = sellingPlanId;

    const giftIds = parseGiftIds(root).filter((id) => id !== variantId);
    const productId = parseInt(input.getAttribute('data-product-id'), 10);
    const giftProps = { _labor_gift: '1' };
    if (productId > 0) giftProps._free_gift_for = String(productId);
    giftIds.forEach((id) => {
      items.push({ id, quantity: 1, properties: giftProps });
    });

    const configuredCode = getConfiguredDiscountCode(root);
    const discountCode = getDiscountCode(root);
    const goToCheckout = !wantsDrawer(root);

    showError(root, '');
    setLoading(button, true);
    window.__cartDrawerUpdating = true;

    laborFetch(cartJsUrl(window.routes?.cart_add_url, '/cart/add'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: 'cart-drawer,cart-icon-bubble' }),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(async ({ ok, data }) => {
        if (!ok) {
          throw new Error((data && (data.description || data.message)) || 'Could not add item to cart.');
        }
        if (window.laborCtaDiscount && typeof window.laborCtaDiscount.remember === 'function') {
          window.laborCtaDiscount.remember({
            code: discountCode,
            variantIds: [variantId],
            giftIds: giftIds,
            productId: productId > 0 ? productId : undefined,
          });
        }
        let sections = data && data.sections;
        if (discountCode) {
          sections = await applyCartDiscountCode(discountCode, sections);
        } else if (configuredCode) {
          sections = await stripCartDiscountCode(configuredCode, sections);
        }
        if (goToCheckout) {
          await redirectToCheckout(discountCode);
          return;
        }
        return openCartDrawer(sections);
      })
      .catch((err) => {
        showError(root, err.message || 'Could not add item to cart. Please try again.');
        setLoading(button, false);
      })
      .finally(() => {
        window.__cartDrawerUpdating = false;
        if (wantsDrawer(root)) setLoading(button, false);
      });
  });

  const laborProxySelector =
    '.comprehensive-approach__cta, .bundle-reasons__cta, .sticky-foundation-bundle__button, [data-scroll-to-product]';

  document.addEventListener(
    'click',
    (event) => {
      const proxy = event.target.closest(laborProxySelector);
      if (!proxy) return;
      const laborCta = document.querySelector('.labor-cta__button');
      if (!laborCta || laborCta.disabled || proxy === laborCta) return;
      event.preventDefault();
      event.stopPropagation();
      laborCta.click();
    },
    true,
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
    document.querySelectorAll(rootSelector).forEach(syncStickyMedia);
  });

  // Safari restores this page from bfcache after checkout-back, with the CTA
  // still disabled/loading because setLoading(false) is skipped on redirect.
  window.addEventListener('pagehide', resetLaborCtas);
  window.addEventListener('pageshow', resetLaborCtas);
})();
